import React, {
  useEffect,
  useState,
} from "react";
import {
  Banner,
  BannerItem,
  Button,
  List,
  ListItem,
} from "@athena/forge";
import { changePassword } from "../../api/Api";
import Header from "../common/Header";
import PasswordInputField from "./PasswordInputField";
import { useTranslation } from "react-i18next";
import { useOktaAuth } from "@okta/okta-react";

import { ChangePasswordRequest } from "../../types/ApiRequest.type";
import { isExpiredToken } from "../../lib/jwt";
import {
  CURRENT_PASSWORD_INPUT_CONTEXT,
  MIN_PASSWORD_LENGTH,
  MAX_PASSWORD_LENGTH,
  NEW_PASSWORD_INPUT_CONTEXT,
} from "../../lib/constants";
import {
  logCancelButtonClick,
  logLandingEvent,
  logSaveButtonClick,
} from "../../lib/amplitude-utils";
import "./EditPassword.scss";


export default function EditPassword(): React.JSX.Element {
  const { t } = useTranslation();

  const [editPasswordError, setEditPasswordError] = useState<string>("");
  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmNewPassword, setConfirmNewPassword] = useState<string>("");
  const { authState } = useOktaAuth();

  const passwordMeetsLengthRequirements = (password: string): boolean => {
    return (
      password !== undefined &&
      password.length >= MIN_PASSWORD_LENGTH &&
      password.length <= MAX_PASSWORD_LENGTH
    );
  };

  const handleCurrentPasswordInputOnChange = (value: string): void => {
    if (editPasswordError) {
      setEditPasswordError("");
    }
    setCurrentPassword(value);
  };

  const handleNewPasswordInputOnChange = (value: string): void => {
    if (editPasswordError) {
      setEditPasswordError("");
    }
    setNewPassword(value);
  };

  const handleConfimNewPasswordInputOnChange = (value: string): void => {
    if (editPasswordError) {
      setEditPasswordError("");
    }
    setConfirmNewPassword(value);
  };

  const handleCancelClick = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    event.preventDefault();
    logCancelButtonClick({ pageName: "EditPassword" });
    window.location.href = "/";
  };

  const handleSaveClick = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    event.preventDefault();
    logSaveButtonClick({ pageName: "EditPassword" });

    // Reload page if accessToken is expired.
    if (
      !authState ||
      !authState.isAuthenticated ||
      !authState.accessToken?.accessToken ||
      isExpiredToken(authState.accessToken.accessToken)
    ) {
      window.location.href = "/editpassword";
    }

    setEditPasswordError("");

    if (!currentPassword || !newPassword || !confirmNewPassword) {
      setEditPasswordError(t("editpassword.blankinputfield.error"));
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setEditPasswordError(t("editpassword.mismatchednewpasswords.error"));
      return;
    }
    if (currentPassword === newPassword) {
      setEditPasswordError(t("editpassword.newpasswordcannotbecurrentpassword.error"));
      return;
    }
    if (!passwordMeetsLengthRequirements(newPassword)) {
      setEditPasswordError(t("editpassword.passwordlengthrequirementnotmet.error"));
      return;
    }

    try {
      const requestArg: ChangePasswordRequest = {
        oldPassword: currentPassword,
        newPassword: newPassword,
      };

      const response = await changePassword(authState, requestArg);
      if (response.status === 403) {
        if (response.errorKey) {
          if (response.inputContext === CURRENT_PASSWORD_INPUT_CONTEXT) {
            setEditPasswordError(t(response.errorKey));
          } else if (response.inputContext === NEW_PASSWORD_INPUT_CONTEXT) {
            setEditPasswordError(t(response.errorKey));
          } else if (!response.inputContext && response.errorKey) {
            setEditPasswordError(t(response.errorKey));
          } else {
            setEditPasswordError(t("app.genericerrormessage"));
          }
        }
        return;
      } else if (response.status !== 200) {
        setEditPasswordError(t("app.genericerrormessage"));
        return;
      }

      sessionStorage.setItem("globalBanner", t("editpassword.update.success"));
      sessionStorage.setItem("globalBannerType", "success");
      window.location.href = "/";
    } catch (error: Error | unknown) {
      if (error instanceof Error) {
        setEditPasswordError(error.message);
      } else {
        setEditPasswordError(t("app.genericerrormessage"));
      }
      return;
    }
  };

  useEffect(() => {
    logLandingEvent({ pageName: "EditPassword" });
  }, []);

  return (
    <>
      <Header
        headerText={t("editpassword.page.title")}
        description={[t("editpassword.page.description")]}
        fontSize="large"
      />

      {editPasswordError && (
        <Banner
          alertType="critical"
          onHide={() => setEditPasswordError("")}
        >
          <BannerItem
            headingTag="h1"
            headerText={t("editpassword.update.error.header")}
          >
            {editPasswordError.split('\n').map((line, index) => (
              <React.Fragment key={index}>
                {line}
                {index < editPasswordError.split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
          </BannerItem>
        </Banner>
      )}

      <div className="uiam-edit-password-container">
        <PasswordInputField
          id="current-password-input"
          data-testid="current-password-input"
          labelKey="editpassword.currentpassword.input.label"
          value={currentPassword}
          autoFocus={true}
          onChange={(value) => handleCurrentPasswordInputOnChange(value)}
        />

        <PasswordInputField
          id="new-password-input"
          data-testid="new-password-input"
          labelKey="editpassword.newpassword.input.label"
          value={newPassword}
          onChange={(value) => handleNewPasswordInputOnChange(value)}
        />

        <div className="fe_u_margin--top-large">
          <Banner alertType="info" className="fe_u_padding--medium">
            <BannerItem
              headerText={t("editpassword.passwordrequirements.banner.header")}
              headingTag="h1"
            >
              <List variant="simple">
                <ListItem
                  uniqueKey="password-req-length"
                  className="fe_u_font-size--small fe_u_padding--top-xsmall"
                >
                  <div className="password-requirement-item">
                    <span>•</span>
                    <span>{t("editpassword.passwordrequirement.minimumlength")}</span>
                  </div>
                </ListItem>
                <ListItem
                  uniqueKey="password-req-maxlength"
                  className="fe_u_font-size--small fe_u_padding--top-xsmall"
                >
                  <div className="password-requirement-item">
                    <span>•</span>
                    <span>{t("editpassword.passwordrequirement.maximumlength")}</span>
                  </div>
                </ListItem>
                <ListItem
                  uniqueKey="password-req-username"
                  className="fe_u_font-size--small fe_u_padding--top-xsmall"
                >
                  <div className="password-requirement-item">
                    <span>•</span>
                    <span>{t("editpassword.passwordrequirement.nousername")}</span>
                  </div>
                </ListItem>
                <ListItem
                  uniqueKey="password-req-firstname"
                  className="fe_u_font-size--small fe_u_padding--top-xsmall"
                >
                  <div className="password-requirement-item">
                    <span>•</span>
                    <span>{t("editpassword.passwordrequirement.nofirstname")}</span>
                  </div>
                </ListItem>
                <ListItem
                  uniqueKey="password-req-lastname"
                  className="fe_u_font-size--small fe_u_padding--top-xsmall"
                >
                  <div className="password-requirement-item">
                    <span>•</span>
                    <span>{t("editpassword.passwordrequirement.nolastname")}</span>
                  </div>
                </ListItem>
                <ListItem
                  uniqueKey="password-req-history"
                  className="fe_u_font-size--small fe_u_padding--top-xsmall"
                >
                  <div className="password-requirement-item">
                    <span>•</span>
                    <span>{t("editpassword.passwordrequirement.nohistorypassword")}</span>
                  </div>
                </ListItem>
                <ListItem
                  uniqueKey="password-req-weak"
                  className="fe_u_font-size--small fe_u_padding--top-xsmall"
                >
                  <div className="password-requirement-item">
                    <span>•</span>
                    <span>{t("editpassword.passwordrequirement.noweakpassword")}</span>
                  </div>
                </ListItem>
              </List>
            </BannerItem>
          </Banner>
        </div>

        <PasswordInputField
          id="confirm-new-password-input"
          data-id="confirm-new-password-input"
          labelKey="editpassword.confirmnewpassword.input.label"
          value={confirmNewPassword}
          onChange={(value) => handleConfimNewPasswordInputOnChange(value)}
        />

        <div className="fe_u_padding--top-large">
          <Button
            className="edit-password-save-button"
            data-testid="edit-password-save-button"
            variant="primary"
            text={t("editpassword.savebutton.text")}
            fullWidth={true}
            onClick={handleSaveClick}
            aria-label={t("editpassword.savebutton.text")}
          />
        </div>
        <div className="fe_u_padding--top-small">
          <Button
            className="edit-password-cancel-button"
            data-testid="edit-password-cancel-button"
            variant="secondary"
            text={t("editpassword.cancelbutton.text")}
            fullWidth={true}
            type="submit"
            onClick={handleCancelClick}
            aria-label={t("editpassword.cancelbutton.text")}
          />
        </div>
      </div>
    </>
  );
}
